import { useState } from 'react';
import { SignOutButton } from '@clerk/clerk-react';
import { trpc } from '../trpc';

/**
 * Full-screen hard lock shown when a user's payment has failed past the grace
 * period (paymentUpdateRequired === true). It replaces the entire app so the only
 * actions are: fix the payment method (Stripe billing portal), contact support,
 * or sign out — mirroring the mobile lockout.
 *
 * Grace-period users (paymentRejected but NOT yet paymentUpdateRequired) are not
 * shown this wall; they keep access with the dismissable nudge dialog. The gating
 * decision lives in __root.tsx.
 *
 * createPortalSession is on the server's payment allowlist, so the fix path keeps
 * working while every other route is blocked.
 */
export function PaymentLockWall() {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const managePayment = trpc.stripe.createPortalSession.useMutation({
    onSuccess: (data: { url?: string }) => {
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setIsRedirecting(false);
      }
    },
    onError: () => {
      setIsRedirecting(false);
    },
  });

  const handleManage = () => {
    setIsRedirecting(true);
    managePayment.mutate({
      // Return the user to the page they're on; a full reload refetches payment
      // status, so the wall clears itself once the card is fixed.
      returnUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      // Send them straight to the payment-method-update page — the actual fix.
      flowType: 'payment_method_update',
    });
  };

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4'>
      <div className='w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl'>
        <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-7 w-7 text-amber-600'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
            />
          </svg>
        </div>
        <h2 className='mb-2 text-xl font-semibold text-slate-900'>
          Your subscription needs attention
        </h2>
        <p className='mb-6 text-sm leading-relaxed text-slate-600'>
          We couldn&apos;t process your latest payment. Update your payment method to
          continue using Trackplicant.
        </p>
        <div className='flex flex-col gap-3'>
          <button
            onClick={handleManage}
            disabled={isRedirecting}
            className='rounded-md bg-indigo-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60'
          >
            {isRedirecting ? 'Opening…' : 'Manage Subscription'}
          </button>
          <div className='rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-left'>
            <p className='mb-2 text-xs font-medium text-slate-500'>
              Need help? Email us:
            </p>
            <div className='space-y-1 text-sm'>
              <div className='flex items-center justify-between gap-4'>
                <span className='text-slate-500'>Support</span>
                <span className='select-all font-medium text-slate-700'>
                  support@trackplicant.com
                </span>
              </div>
              <div className='flex items-center justify-between gap-4'>
                <span className='text-slate-500'>Billing</span>
                <span className='select-all font-medium text-slate-700'>
                  billing@trackplicant.com
                </span>
              </div>
            </div>
          </div>
          <SignOutButton redirectUrl='/'>
            <button className='px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700'>
              Sign Out
            </button>
          </SignOutButton>
        </div>
      </div>
    </div>
  );
}
